import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveStep } from "../../services/stepService"
import { markStepAsVisited } from '../../redux/slice/profileSlice';
// import { markStepAsVisited } from '../../services/commonService';
import { useDispatch } from 'react-redux';

const StepProgressView = ({ number }) => {
  const { session } = useSelector((state) => state.authProvider);
  const { activeBookId } = useSelector((state) => state.stepFlow);
  const { myBooks = [], data: profileData } = useSelector((state) => state.profile);
  const { coverImages = [] } = useSelector(state => state.sketch)
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const stepMap = {
    'Create_New_Book': 1,
    'Upload_Your_Cover_Photo': 2,
    'Upload_Images': 3,
    'Design_Your_Cover_Page': 4,
    'Review_Your_Book': 5
  };

  const steps = [
    { number: 1, label: 'Enter Title', stepCode: 'Create_New_Book', route: '/create-book' },
    { number: 2, label: 'Upload Cover', stepCode: 'Upload_Your_Cover_Photo', route: '/dashboard' },
    { number: 3, label: 'Choose Photos', stepCode: 'Upload_Images', route: '/create' },
    { number: 4, label: 'Design Cover', stepCode: 'Design_Your_Cover_Page', route: '/cover-image' },
    { number: 5, label: 'Preview Book', stepCode: 'Review_Your_Book', route: '/review' }
  ];

  // Get active book
  const getActiveBook = () => myBooks.find(book => book._id === activeBookId);
  const getProfileBook = () => profileData?.bookData?.find(book => book._id === activeBookId)
  const latestStepCode = getProfileBook()?.latestStepCode?.code

  // Determine active step
  const determineActiveStep = () => {
    // First priority: URL stepCode
    const params = new URLSearchParams(window.location.search);
    const urlStepCode = params.get("stepCode");
    if (urlStepCode && stepMap[urlStepCode]) {
      return stepMap[urlStepCode];
    }

    // Second priority: latestStepCode from profile
    if (latestStepCode && stepMap[latestStepCode]) {
      return stepMap[latestStepCode];
    }

    // Fallback logic based on cover images
    if (!coverImages || coverImages.length === 0) {
      return 2; // Upload_Your_Cover_Photo
    }

    return 1; // Create_New_Book
  };

  const [activeStep, setActiveStep] = useState(determineActiveStep());

  // Get visited step numbers from book's completedSteps
  const getVisitedSteps = () => {
    const activeBook = getActiveBook();
    if (!activeBook || !activeBook.completedSteps) return [];
    
    // Convert step codes to step numbers
    return activeBook.completedSteps
      .map(stepCode => stepMap[stepCode])
      .filter(stepNum => stepNum !== undefined);
  };

  // Update activeStep when URL changes or latestStepCode changes
  useEffect(() => {
    setActiveStep(determineActiveStep());
  }, [latestStepCode, coverImages]);

  // Mark current step as visited on mount
  useEffect(() => {
    if (activeBookId && activeStep) {
      const visitedSteps = getVisitedSteps();
      if (!visitedSteps.includes(activeStep)) {
        // Get the step code for the current active step
        const currentStepCode = steps.find(s => s.number === activeStep)?.stepCode;
        if (currentStepCode) {
          dispatch(markStepAsVisited({ id: activeBookId, currentStepCode }))
          // markStepAsVisited(currentStepCode, activeBookId);
        }
      }
    }
  }, [activeStep, activeBookId]);

  // Check if a step is clickable
  const isStepClickable = (stepNumber) => {
    if (stepNumber === activeStep) return false; // Current step not clickable
    
    // Check if cover images are required and exist
    const requiresCoverImages = stepNumber >= 3; // Steps 3, 4, 5 need cover images
    if (requiresCoverImages && (!coverImages || coverImages.length === 0)) {
      return false; // Block navigation if no cover images
    }
    
    const visitedSteps = getVisitedSteps();
    
    // Allow backward navigation or forward to previously visited steps
    return stepNumber <= activeStep || visitedSteps.includes(stepNumber);
  };

  const handleStepClick = (stepNumber, stepCode, route) => {
    if (isStepClickable(stepNumber)) {
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: stepCode,
      });
      dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: stepCode }))
      // markStepAsVisited(stepCode, activeBookId);
      navigate(`${route}?stepCode=${stepCode}`);
    }
  };

  const visitedSteps = getVisitedSteps();

  // Get circle style based on step position relative to active step
  const getCircleStyle = (stepNumber) => {
    if (stepNumber <= activeStep) {
      // Current step and all previous steps - always filled pink
      return 'bg-md-pink';
    } else {
      // ALL steps after current - always white (regardless of completedSteps)
      return 'bg-white';
    }
  };

  // Get border thickness based on step position
  const getBorderStyle = (stepNumber) => {
    if (stepNumber === activeStep) {
      // Active step - thicker border
      return 'border-4';
    } else {
      // All other steps - normal border
      return 'border-2';
    }
  };

  const getLineStyle = (stepNumber) => {
    if (stepNumber < activeStep) {
      return 'bg-md-pink h-1';
    } else {
      return 'bg-black h-1';
    }
  };

  return (
    <div className="py-4 w-full">
      <div className="relative flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.number} className="flex flex-col items-center relative z-10 flex-shrink flex-grow">
            <div
              onClick={() => handleStepClick(step.number, step.stepCode, step.route)}
              className={`w-4 md:w-8 h-4 md:h-8 rounded-full flex items-center justify-center transition-all duration-300 border-md-pink ${
                getBorderStyle(step.number)
              } ${
                getCircleStyle(step.number)
              } ${
                isStepClickable(step.number)
                  ? 'cursor-pointer' 
                  : 'cursor-default'
              }`}
            >
            </div>

            <div className="text-xs md:text-sm text-black mt-2 text-center">
              {step.label}
            </div>

            {index < steps.length - 1 && (
              <div className="absolute top-[6px] md:top-4 left-8 md:left-1/2 w-full -z-10">
                <div
                  className={`transition-all duration-300 ${
                    getLineStyle(step.number)
                  }`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepProgressView;